import { AlertCircle, Check, Loader, Mic, Volume2, X } from 'lucide-react';
import React, { useState } from 'react';
import './PodcastGenerationModal.css';

const PodcastGenerationModal = ({
    isOpen,
    onClose,
    post,
    quotaInfo,
    onGenerate
}) => {
    const [options, setOptions] = useState({
        voice: 'female',
        hasIntroduction: true,
        hasConclusion: true,
        pace: 'normal',
        tone: 'professional',
        targetDuration: 'auto',
        backgroundMusic: false,
        musicType: 'ambient'
    });

    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    const handleGenerate = async () => {
        try {
            setError(null);
            setIsGenerating(true);
            await onGenerate(options);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to generate podcast');
        } finally {
            setIsGenerating(false);
        }
    };

    const hasQuota = quotaInfo && quotaInfo.remaining > 0;

    if (!isOpen) return null;

    return (
        <div
            className="podcast-modal-overlay"
            onClick={onClose}
        >
            <div
                className="podcast-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="podcast-modal-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <Mic className="icon" />
                        </div>
                        <div>
                            <h2>Generate AI Podcast</h2>
                            <p>Transform your blog into audio</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isGenerating}
                        className="close-button"
                    >
                        <X />
                    </button>
                </div>

                {/* Content */}
                <div className="podcast-modal-body">
                    {/* Quota Info */}
                    {quotaInfo && (
                        <div className={`quota-info ${hasQuota ? 'has-quota' : 'no-quota'}`}>
                            <div className="quota-content">
                                {hasQuota ? (
                                    <Check className="quota-icon" />
                                ) : (
                                    <AlertCircle className="quota-icon" />
                                )}
                                <div>
                                    <p className="quota-title">
                                        {hasQuota ? `${quotaInfo.remaining} podcast${quotaInfo.remaining !== 1 ? 's' : ''} remaining` : 'No podcasts remaining'}
                                    </p>
                                    <p className="quota-subtitle">
                                        {quotaInfo.used} of {quotaInfo.limit} used this month
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            <AlertCircle />
                            <p>{error}</p>
                        </div>
                    )}

                    {/* Post Preview */}
                    <div className="post-preview">
                        <h3>{post.title}</h3>
                        <p>{post.excerpt}</p>
                    </div>

                    {/* Voice Selection */}
                    <div className="option-group">
                        <label>Voice Type</label>
                        <div className="voice-options">
                            <button
                                onClick={() => setOptions({ ...options, voice: 'female' })}
                                disabled={isGenerating}
                                className={`option-button ${options.voice === 'female' ? 'selected' : ''}`}
                            >
                                <Volume2 />
                                <div className="option-text">
                                    <div className="option-title">Female Voice</div>
                                    <div className="option-desc">Natural & engaging</div>
                                </div>
                            </button>

                            <button
                                onClick={() => setOptions({ ...options, voice: 'male' })}
                                disabled={isGenerating}
                                className={`option-button ${options.voice === 'male' ? 'selected' : ''}`}
                            >
                                <Volume2 />
                                <div className="option-text">
                                    <div className="option-title">Male Voice</div>
                                    <div className="option-desc">Authoritative & clear</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Tone Selection */}
                    <div className="option-group">
                        <label>Tone & Style</label>
                        <div className="tone-options">
                            {['professional', 'casual', 'enthusiastic'].map((tone) => (
                                <button
                                    key={tone}
                                    onClick={() => setOptions({ ...options, tone })}
                                    disabled={isGenerating}
                                    className={`tone-button ${options.tone === tone ? 'selected' : ''}`}
                                >
                                    {tone.charAt(0).toUpperCase() + tone.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Duration Selection */}
                    <div className="option-group">
                        <label>Target Duration</label>
                        <div className="tone-options">
                            {[
                                { value: 'short', label: 'Short (3-5 min)' },
                                { value: 'medium', label: 'Medium (5-10 min)' },
                                { value: 'long', label: 'Long (10-15 min)' },
                                { value: 'auto', label: 'Auto' }
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    onClick={() => setOptions({ ...options, targetDuration: value })}
                                    disabled={isGenerating}
                                    className={`tone-button ${options.targetDuration === value ? 'selected' : ''}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Pace Selection */}
                    <div className="option-group">
                        <label>Speaking Pace</label>
                        <div className="tone-options">
                            {['slow', 'normal', 'fast'].map((pace) => (
                                <button
                                    key={pace}
                                    onClick={() => setOptions({ ...options, pace })}
                                    disabled={isGenerating}
                                    className={`tone-button ${options.pace === pace ? 'selected' : ''}`}
                                >
                                    {pace.charAt(0).toUpperCase() + pace.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Options */}
                    <div className="checkbox-options">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={options.hasIntroduction}
                                onChange={(e) => setOptions({ ...options, hasIntroduction: e.target.checked })}
                                disabled={isGenerating}
                            />
                            <div>
                                <div className="checkbox-title">Include Introduction</div>
                                <div className="checkbox-desc">Add intro to set context</div>
                            </div>
                        </label>

                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={options.hasConclusion}
                                onChange={(e) => setOptions({ ...options, hasConclusion: e.target.checked })}
                                disabled={isGenerating}
                            />
                            <div>
                                <div className="checkbox-title">Include Conclusion</div>
                                <div className="checkbox-desc">Add summary and CTA</div>
                            </div>
                        </label>

                        {/* <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={options.backgroundMusic}
                                onChange={(e) => setOptions({ ...options, backgroundMusic: e.target.checked })}
                                disabled={isGenerating}
                            />
                            <div>
                                <div className="checkbox-title">Background Music</div>
                                <div className="checkbox-desc">Add ambient background music</div>
                            </div>
                        </label> */}
                    </div>

                    {/* Music Type Selection (shown only if background music is enabled) */}
                    {/* {options.backgroundMusic && (
                        <div className="option-group">
                            <label>Music Type</label>
                            <div className="tone-options">
                                {['ambient', 'upbeat', 'corporate', 'cinematic'].map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setOptions({ ...options, musicType: type })}
                                        disabled={isGenerating}
                                        className={`tone-button ${options.musicType === type ? 'selected' : ''}`}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )} */}
                </div>

                {/* Footer */}
                <div className="podcast-modal-footer">
                    <button
                        onClick={onClose}
                        disabled={isGenerating}
                        className="cancel-button"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleGenerate}
                        // disabled={isGenerating || !hasQuota}
                        className="generate-button"
                    >
                        {isGenerating ? (
                            <>
                                <Loader className="spinner" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Mic />
                                Generate Podcast
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PodcastGenerationModal;


